import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import type { StageFigures } from '../../../hooks/pipelineTracker/usePipelineReport';
import { money, num } from './pipelineParts';
import { HousePanel } from './trackerKit';

/**
 * "Where the N active deals sit today", from the Evoke mockup.
 *
 * The one view a principal actually opens the tab for: every active stage on a
 * single line, with the count, the value, how long deals have been sitting, and
 * how many are past 30 and 90 days. The funnel below it answers "what shape is
 * the book"; this answers "where is my money stuck", which is the question that
 * gets asked first.
 *
 * A STRIP, not a second funnel. It is deliberately dense and horizontal so the
 * whole book is legible without scrolling, and it carries no bars, because two
 * bar charts stacked on one screen makes the reader compare the wrong pair.
 *
 * EVERY NUMBER IS FROM THE SNAPSHOT. The ageing counts are the same cumulative
 * buckets the funnel uses, so a deal past 90 days is also counted in "over 30",
 * which is how a practice reads it out loud.
 */

/** A stage is worth flagging once a third of it is past 90 days. */
const CRITICAL_SHARE = 0.33;

/** ...or once half of it is past 30. */
const WARNING_SHARE = 0.5;

type Tone = 'critical' | 'warning' | 'calm';

function toneFor(stage: StageFigures): Tone {
  if (stage.dealCount === 0) return 'calm';
  if (stage.over90Count / stage.dealCount >= CRITICAL_SHARE) return 'critical';
  if (stage.over30Count / stage.dealCount >= WARNING_SHARE) return 'warning';
  return 'calm';
}

const CARD_TONE: Record<Tone, string> = {
  critical: 'border-red-500/50',
  warning: 'border-amber-500/40',
  calm: 'border-gray-700/70',
};

const PILL_TONE: Record<Tone, string> = {
  critical: 'bg-red-500/15 text-red-300',
  warning: 'bg-amber-500/15 text-amber-300',
  calm: 'bg-gray-700/60 text-gray-300',
};

export interface StageStripProps {
  /** Active stages only, in funnel order. */
  stages: StageFigures[];
  totalActiveDeals: number;
  /** Deals that converted and left the funnel, shown as a footnote not a card. */
  postSettlement: { count: number; valueCents: number };
  /** The panel's accent: its group's colour on the jump bar. Brand blue by default. */
  accent?: string;
  /** The scope chip, e.g. "As at 12 Sept 2026". */
  chip?: React.ReactNode;
}

/**
 * Gentle auto-scroll, so a book with more stages than fit shows its far end without anyone
 * reaching for the scrollbar (founder-approved mockup, 5 Oct 2026). It ping-pongs end to end at
 * this speed and only while the cards overflow.
 */
const AUTO_SCROLL_PX_PER_SECOND = 27;

/** How long the strip stays still after each kind of interaction ends, in milliseconds. */
const RESUME_AFTER = { hover: 600, focus: 1500, drag: 1500, touch: 3000, wheel: 3000, arrow: 3500 } as const;

/** An arrow moves the strip by this share of its visible width. */
const ARROW_STEP = 0.7;

/**
 * How often the animation re-asks the browser whether the pointer is really over the strip. Enter
 * and leave events are not guaranteed: a page that scrolls the strip away under a still pointer, or
 * a pointer that leaves while the window is in the background, can leave "hovered" set for good.
 */
const HOVER_RECHECK_MS = 250;

/** Focus that lands this soon after a press inside the strip came from the press, not the keyboard. */
const POINTER_FOCUS_WINDOW_MS = 500;

/** Whether the browser says the element matches, or null when it cannot answer. */
function matchesSafely(el: Element, selector: string): boolean | null {
  try {
    return el.matches(selector);
  } catch {
    return null;
  }
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const hasMatchMedia = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/** Follows the operating system setting live, so turning it on mid-session stops the motion. */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => hasMatchMedia() && window.matchMedia(REDUCED_MOTION_QUERY).matches);
  useEffect(() => {
    if (!hasMatchMedia()) return undefined;
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduced(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

const StageStrip: React.FC<StageStripProps> = ({ stages, totalActiveDeals, postSettlement, accent, chip }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const [overflowing, setOverflowing] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Why the strip is still. Live reasons (hovered, focused, touched, dragged) hold it for as long
  // as they last; each one, once it ends, holds it a little longer through holdUntil, so the strip
  // never moves out from under a reader the instant they let go.
  const hold = useRef({ hover: false, focus: false, touch: false, drag: false, holdUntil: 0 });
  const dragStart = useRef<{ x: number; scrollLeft: number } | null>(null);
  const lastPressAt = useRef(Number.NEGATIVE_INFINITY);
  const holdFor = (ms: number) => {
    hold.current.holdUntil = Math.max(hold.current.holdUntil, performance.now() + ms);
  };

  /**
   * Only keyboard focus holds the strip until it leaves. A mouse click on an arrow or a card also
   * focuses something inside the strip, and that focus stays put until the reader clicks elsewhere,
   * so treating it as a reason to stay still stopped the strip for good after a single click. A
   * click already holds the strip for a while through the arrow and drag delays.
   */
  const isKeyboardFocus = (target: EventTarget): boolean => {
    if (performance.now() - lastPressAt.current < POINTER_FOCUS_WINDOW_MS) return false;
    if (!(target instanceof Element)) return true;
    return matchesSafely(target, ':focus-visible') ?? true;
  };

  // Overflow is measured, never assumed: three stages on a wide screen fit, and a strip that fits
  // must neither move nor offer arrows that do nothing.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => setOverflowing(el.scrollWidth - el.clientWidth > 1);
    measure();
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [stages.length]);

  const autoScroll = overflowing && !reducedMotion;

  useEffect(() => {
    if (!autoScroll) return undefined;
    // The position is kept as a float here: scrollLeft rounds, and at 27px/s a frame moves less
    // than one pixel, so reading it back each frame would never move at all.
    // The element is looked up every frame, never captured once: if the strip's scroller is
    // replaced (no stages for a moment, then stages again) a captured one would be a detached node
    // that the loop goes on scrolling while the visible strip sits still.
    let el: HTMLDivElement | null = null;
    let x = 0;
    let direction = 1;
    let last: number | null = null;
    let lastHoverCheck = Number.NEGATIVE_INFINITY;
    let frame = 0;
    const tick = () => {
      frame = window.requestAnimationFrame(tick);
      const now = performance.now();
      // Capped so a tab returning from the background does not jump the strip.
      const seconds = last === null ? 0 : Math.min(0.1, (now - last) / 1000);
      last = now;
      const current = scrollerRef.current;
      if (!current) return;
      if (current !== el) {
        el = current;
        x = el.scrollLeft;
      }
      const h = hold.current;
      const wrapper = wrapperRef.current;
      if (h.hover && wrapper && now - lastHoverCheck >= HOVER_RECHECK_MS) {
        lastHoverCheck = now;
        if (matchesSafely(wrapper, ':hover') === false) {
          h.hover = false;
          holdFor(RESUME_AFTER.hover);
        }
      }
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0 || h.hover || h.focus || h.touch || h.drag || now < h.holdUntil) {
        // Follow wherever the reader left it, so motion resumes from there.
        x = el.scrollLeft;
      } else {
        x += AUTO_SCROLL_PX_PER_SECOND * seconds * direction;
        if (x >= max) {
          x = max;
          direction = -1;
        } else if (x <= 0) {
          x = 0;
          direction = 1;
        }
        el.scrollLeft = x;
      }
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [autoScroll]);

  // A reader who leaves the window or the tab is not hovering or touching the strip any more, and
  // the leave or end event that would have said so may never arrive.
  useEffect(() => {
    const release = () => {
      const h = hold.current;
      if (h.hover || h.touch) holdFor(RESUME_AFTER.hover);
      h.hover = false;
      h.touch = false;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') release();
    };
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Drag to scroll with the mouse. The window listeners exist only for the length of a drag, so a
  // release outside the strip still ends it, and so does a move with no button down or the window
  // losing focus, which is how a release outside the browser shows up.
  useEffect(() => {
    if (!dragging) return undefined;
    const onUp = () => {
      dragStart.current = null;
      hold.current.drag = false;
      holdFor(RESUME_AFTER.drag);
      setDragging(false);
    };
    const onMove = (event: MouseEvent) => {
      const el = scrollerRef.current;
      const start = dragStart.current;
      if (!el || !start) return;
      if ((event.buttons & 1) === 0) {
        onUp();
        return;
      }
      event.preventDefault();
      el.scrollLeft = start.scrollLeft - (event.clientX - start.x);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('blur', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
    };
  }, [dragging]);

  if (stages.length === 0) return null;

  const step = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    holdFor(RESUME_AFTER.arrow);
    const left = direction * el.clientWidth * ARROW_STEP;
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
    } else {
      el.scrollLeft += left;
    }
  };

  /**
   * The merge sentence, computed rather than asserted.
   *
   * The mockup claimed "Doc Collection combines its Portal, Client Portal, PIP,
   * Issued and Returned variants". That is true of Evoke and of nobody else, so
   * here it is derived: how many stages are merging more than one spelling, and
   * how many spellings in total. A tenant whose provider uses one name per
   * stage is told nothing, because there is nothing to tell.
   */
  const merging = stages.filter((s) => s.rawNameCount > 1);
  const mergedNames = merging.reduce((n, s) => n + s.rawNameCount, 0);

  return (
    <HousePanel title={`Where the ${num(totalActiveDeals)} active deals sit today`} icon={LayoutGrid} color={accent} badge={chip}>

      {/*
        Horizontal scroll rather than wrapping. A practice with twelve stages
        should see a twelfth card off the edge and scroll to it, not have the
        ladder silently rearrange itself into a second row that reads as a
        separate group. The page body never scrolls sideways; this container does.
      */}
      <div
        ref={wrapperRef}
        data-testid="stage-strip"
        className="relative"
        onMouseEnter={() => { hold.current.hover = true; }}
        onMouseLeave={() => { hold.current.hover = false; holdFor(RESUME_AFTER.hover); }}
        onMouseDownCapture={() => { lastPressAt.current = performance.now(); }}
        onTouchStartCapture={() => { lastPressAt.current = performance.now(); }}
        onFocus={(event) => { hold.current.focus = isKeyboardFocus(event.target); }}
        onBlur={() => {
          if (hold.current.focus) holdFor(RESUME_AFTER.focus);
          hold.current.focus = false;
        }}
        onTouchStart={() => { hold.current.touch = true; }}
        onTouchEnd={() => { hold.current.touch = false; holdFor(RESUME_AFTER.touch); }}
        onTouchCancel={() => { hold.current.touch = false; holdFor(RESUME_AFTER.touch); }}
        onWheel={() => holdFor(RESUME_AFTER.wheel)}
      >
        {overflowing && (
          <>
            {/* Edge fades: a card cut off at the edge reads as "there is more this way". */}
            <div
              aria-hidden="true"
              data-testid="stage-strip-fade"
              className="pointer-events-none absolute bottom-1 left-0 top-0 z-[1] w-10 bg-gradient-to-r from-gray-900/95 to-transparent"
            />
            <div
              aria-hidden="true"
              data-testid="stage-strip-fade"
              className="pointer-events-none absolute bottom-1 right-0 top-0 z-[1] w-10 bg-gradient-to-l from-gray-900/95 to-transparent"
            />
            <button
              type="button"
              aria-label="Scroll stages left"
              onClick={() => step(-1)}
              className="absolute left-1 top-1/2 z-[2] flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-700 bg-gray-900 text-gray-200 shadow-lg transition-colors hover:border-blue-600 hover:bg-blue-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Scroll stages right"
              onClick={() => step(1)}
              className="absolute right-1 top-1/2 z-[2] flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-700 bg-gray-900 text-gray-200 shadow-lg transition-colors hover:border-blue-600 hover:bg-blue-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </>
        )}
        <div
          ref={scrollerRef}
          data-testid="stage-strip-scroller"
          data-autoscroll={autoScroll ? 'on' : 'off'}
          // Focusable so the keyboard can scroll it; keyboard focus also pauses the motion.
          tabIndex={overflowing ? 0 : undefined}
          role={overflowing ? 'region' : undefined}
          aria-label={overflowing ? 'Stages, scroll sideways' : undefined}
          onMouseDown={(event) => {
            const el = scrollerRef.current;
            if (event.button !== 0 || !el || !overflowing) return;
            dragStart.current = { x: event.clientX, scrollLeft: el.scrollLeft };
            hold.current.drag = true;
            setDragging(true);
          }}
          className={`-mx-1 overflow-x-auto px-1 pb-1 ${
            overflowing ? (dragging ? 'cursor-grabbing select-none' : 'cursor-grab') : ''
          }`}
        >
          <div className="flex min-w-max gap-2">
            {stages.map((stage) => {
              const tone = toneFor(stage);
              const flagged = stage.over90Count > 0 ? stage.over90Count : stage.over30Count;
              const flagLabel = stage.over90Count > 0 ? 'over 90d' : 'over 30d';

              return (
                <div
                  key={stage.canonicalKey}
                  className={`min-w-[9.5rem] flex-1 rounded-lg border bg-gray-900/50 p-3 ${CARD_TONE[tone]}`}
                >
                  <div
                    className="mb-1.5 text-[0.625rem] font-semibold uppercase leading-tight tracking-wider text-gray-400"
                    title={
                      stage.rawNameCount > 1
                        ? `${stage.rawNameCount} Salestrekker stage names merge into this one`
                        : undefined
                    }
                  >
                    {stage.label}
                    {stage.rawNameCount > 1 && (
                      <span className="ml-1 font-normal normal-case tracking-normal text-gray-500">
                        ({stage.rawNameCount})
                      </span>
                    )}
                  </div>

                  <div className="text-2xl font-semibold tabular-nums leading-none text-gray-100">
                    {num(stage.dealCount)}
                  </div>
                  <div className="mt-0.5 text-xs tabular-nums text-gray-400">
                    {money(stage.totalValueCents)}
                  </div>

                  <div className="mt-2 border-t border-gray-700/60 pt-2 text-[0.6875rem] text-gray-500">
                    {stage.medianAgeDays === null ? (
                      // Never a zero. No age recorded is a different fact from
                      // "moved today", and printing 0d would assert the wrong one.
                      <span>no age recorded</span>
                    ) : (
                      <span>
                        median <b className="tabular-nums text-blue-400">{stage.medianAgeDays}d</b> in stage
                      </span>
                    )}
                  </div>

                  {flagged > 0 && (
                    <div
                      className={`mt-1.5 inline-block rounded px-1.5 py-0.5 text-[0.625rem] font-semibold tabular-nums ${PILL_TONE[tone]}`}
                    >
                      {num(flagged)} {flagLabel}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {overflowing && (
        <p className="mt-1.5 text-[0.6875rem] text-gray-500" data-testid="stage-strip-hint">
          {num(stages.length)} {stages.length === 1 ? 'stage' : 'stages'}
          {!reducedMotion && <> &middot; moves on its own, pauses when you hover</>}
          {' '}&middot; drag, scroll or use the arrows
        </p>
      )}

      <p className="mt-3 text-xs leading-relaxed text-gray-500">
        {merging.length > 0 && (
          <>
            Stage dialects merged for counting: {num(mergedNames)} Salestrekker stage names collapse
            into {num(merging.length)}{' '}
            {merging.length === 1 ? 'stage' : 'stages'} above. Raw stage names are preserved
            underneath and every deal keeps the value the export gave it.{' '}
          </>
        )}
        {postSettlement.count > 0 && (
          <>
            A further {num(postSettlement.count)} deals sit in post-settlement care (
            {money(postSettlement.valueCents)}) and are outside the funnel.
          </>
        )}
      </p>
    </HousePanel>
  );
};

export default StageStrip;
